import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataListTableElement, DLT_DATA_TYPES } from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';

import { AttachmentStatus, FILE_TYPES } from '@shared/constants';
import { IAttachmentDocument } from '@shared/interfaces';
import { generateFileTypeRegex, viewDocument } from '@shared/utils';

import { SnackbarService } from '../../../../services/snackbar.service';
import { noWhitespaceValidator } from '../../../core/helpers';

export interface IBrokerDocumentUploaderPopup {
  maxSize?: number;
  allowedTypesNames?: string;
  allowedTypesRegex?: RegExp;
  fileCount?: number;
  title: string;
  subtitle: string;
  icon?: string;
  isResend?: boolean;
  fieldName?: string;
  currencyCode?: string;
  totalPremium?: number;
  isReceivedQuotationFlow?: boolean;
}

const ACTIONS = {
  DELETE: 'DELETE',
  VIEW_DOCUMENT: 'VIEW DOCUMENT',
};

@Component({
  selector: 'app-upload-document',
  templateUrl: './upload-document.component.html',
  styleUrls: ['./upload-document.component.scss'],
})
export class UploadDocumentComponent implements OnInit {
  documents: IAttachmentDocument[] = [];
  documentsDataList: DataListTableElement[] = [];
  files: File[] = [];

  uploadedFiles: string[] = [];
  imgPath: string;
  file_name = new FormControl(null, [Validators.required]);
  maxSize = 10;
  fileCount = 100;
  allowedTypesNames = '*.docx/ *.xlsx/ *.jpeg/ *.png/ *.csv and *.pdf';
  allowedTypesRegex = generateFileTypeRegex([
    FILE_TYPES.IMAGE,
    FILE_TYPES.DOCS,
    FILE_TYPES.CSV,
    FILE_TYPES.EXCEL,
    FILE_TYPES.PDF,
  ]);

  uploadForm = new FormGroup({
    isPackageWise: new FormControl(false),
    quotationNumber: new FormControl(null, [
      Validators.required,
      noWhitespaceValidator,
    ]),
    remarks: new FormControl<string>({ value: null, disabled: true }, [
      Validators.required,
    ]),
  });

  packages = new FormArray<FormGroup>([]);

  fieldName = 'Quotation Number';

  constructor(
    public translate: TranslateService,
    private matDialog: MatDialogRef<UploadDocumentComponent>,
    private snackBar: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: IBrokerDocumentUploaderPopup
  ) {}

  ngOnInit(): void {
    if (this.data.maxSize) {
      this.maxSize = this.data.maxSize;
    }
    if (this.data.allowedTypesNames) {
      this.allowedTypesNames = this.data.allowedTypesNames;
    }
    if (this.data.allowedTypesRegex) {
      this.allowedTypesRegex = this.data.allowedTypesRegex;
    }
    if (this.data.fileCount) {
      this.fileCount = this.data.fileCount;
    }
    if (this.data.isResend) {
      this.uploadForm.controls.remarks.enable();
    }
    if (this.data.fieldName) {
      this.fieldName = this.data.fieldName;
    }

    if (this.data.isReceivedQuotationFlow) {
      this.uploadForm.controls.isPackageWise.valueChanges.subscribe((value) => {
        if (value) {
          this.uploadForm.controls.quotationNumber.disable();
          this.uploadForm.controls.quotationNumber.setValue(null);
          this.files = [];
          this.documents = [];
          this.documentsDataList = [];
          this.fileCount = 100;
          if (this.packages.length === 0) {
            this.addPackage();
          }
        } else {
          this.uploadForm.controls.quotationNumber.enable();
          while (this.packages.length !== 0) {
            this.packages.removeAt(0);
          }
        }
      });
    }
  }

  triggerFileSelected(file: File) {
    this.files.push(file);
    this.addToDocumentsList(file);
    this.fileCount = this.fileCount - 1;
  }

  private addToDocumentsList(file: File) {
    const quotationNumber = this.uploadForm.get('quotationNumber')?.value;
    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const timeFormatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const currentDate = new Date();
    const formattedDate = dateFormatter.format(currentDate);
    const formattedTime = timeFormatter.format(currentDate);
    const fullDateTime = `${formattedDate} (${formattedTime})`;

    const documentElement: DataListTableElement = {
      icon: 'description',
      data: [
        {
          key: file.name,
          value: `${quotationNumber}`,
          type: DLT_DATA_TYPES.PLAIN,
        },
        {
          key: 'Uploaded Date',
          value: `\n${fullDateTime}`,
          type: DLT_DATA_TYPES.PLAIN,
        },
      ],
      actions: [
        {
          icon: 'open_in_new',
          action: ACTIONS.VIEW_DOCUMENT,
          tooltip: 'Vew Document',
          face: '',
          data: this.files,
        },
        {
          icon: 'delete',
          action: ACTIONS.DELETE,
          tooltip: 'Delete',
          face: '',
          data: this.files,
        },
      ],
      loading: false,
      expanded: false,
      index: this.documentsDataList.length,
      originalData: file,
    };

    this.documentsDataList.push(documentElement);
    this.documents.push({
      file,
      status: AttachmentStatus.PENDING_UPLOAD,
      name: file.name,
      type: file.type,
      uploaded_date: new Date(),
    });
  }

  action(e: { action: string; index: number }) {
    if (e.action === ACTIONS.DELETE) {
      this.deleteDocument(e.index);
    }
    if (e.action === ACTIONS.VIEW_DOCUMENT) {
      this.viewDocumentFunction(e.index);
    }
  }
  private viewDocumentFunction(index: number) {
    const foundDoc = this.documents?.[index];
    const file = foundDoc?.file;
    viewDocument(file);
  }
  private deleteDocument(index: number) {
    this.documentsDataList.splice(index, 1);
    this.documents.splice(index, 1);
    this.files.splice(index, 1);
    this.fileCount = this.fileCount + 1;

    // Update indexes after deletion
    this.documentsDataList.forEach((doc, i) => {
      doc.index = i;
    });
  }

  onCancel(): void {
    this.matDialog.close();
  }

  addPackage() {
    this.packages.push(
      new FormGroup({
        packageName: new FormControl('', [
          Validators.required,
          noWhitespaceValidator,
        ]),
        quotationNumber: new FormControl('', [
          Validators.required,
          noWhitespaceValidator,
        ]),
        file: new FormControl(null, [Validators.required]),
      })
    );
  }

  removePackage(index: number) {
    this.packages.removeAt(index);
  }

  onPackageFileSelected(event: any, index: number) {
    const fileList: FileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];

      if (file.type.match(this.allowedTypesRegex) == null) {
        this.snackBar.error(`Only ${this.allowedTypesNames} are supported.`);
        event.target.value = '';
        return;
      }

      if (file.size > this.maxSize * 1024 * 1024) {
        this.snackBar.error(`File size should be less than ${this.maxSize}MB.`);
        event.target.value = '';
        return;
      }

      this.packages.at(index).get('file').setValue(file);
      event.target.value = '';
    }
  }

  removePackageFile(index: number) {
    this.packages.at(index).get('file').setValue(null);
  }

  get isSaveEnabled(): boolean {
    if (this.uploadForm.controls.isPackageWise?.value) {
      return (
        this.uploadForm.valid && this.packages.valid && this.packages.length > 0
      );
    }
    return this.uploadForm.valid && this.documentsDataList.length !== 0;
  }

  onSave() {
    if (this.uploadForm.controls.isPackageWise?.value) {
      if (this.uploadForm.valid && this.packages.valid) {
        const result = {
          isPackageWise: true,
          packages: this.packages.value.map((pkg) => ({
            packageName: pkg.packageName,
            quotationNumber: pkg.quotationNumber,
            file: pkg.file,
          })),
          ...(this.data.isResend
            ? { remarks: this.uploadForm.controls.remarks?.value }
            : {}),
        };
        this.matDialog.close(result);
      }
    } else {
      if (this.uploadForm.valid && this.files.length > 0) {
        const result = {
          isPackageWise: false,
          files: this.files,
          quotationNumber: this.uploadForm.controls.quotationNumber?.value,
          ...(this.data.isResend
            ? { remarks: this.uploadForm.controls.remarks?.value }
            : {}),
        };
        this.matDialog.close(result);
      }
    }
  }
}
